import type { WhatsappProfile } from "./whatsapp/profile";

export const CHANNEL_TYPE = {
  INSTAGRAM: "instagram",
  WHATSAPP: "whatsapp",
  MESSENGER: "messenger",
  TIKTOK: "tiktok",
  PHONE: "phone",
} as const;

export type ChannelType = (typeof CHANNEL_TYPE)[keyof typeof CHANNEL_TYPE];

export const CHANNEL_CONNECTION_STEP = {
  WAITING: "waiting",
  RETRIEVING: "retrieving",
  REGISTERING: "registering",
  SETTING_UP: "setting_up",
  SYNCING: "syncing",
} as const;

export type ChannelConnectionStep =
  (typeof CHANNEL_CONNECTION_STEP)[keyof typeof CHANNEL_CONNECTION_STEP];

// Channel-level connection state (provider-agnostic).
// null/absent means the channel is connected.
export const CHANNEL_CONNECTION_STATE = {
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
} as const;

export type ChannelConnectionState =
  (typeof CHANNEL_CONNECTION_STATE)[keyof typeof CHANNEL_CONNECTION_STATE];

export type ChannelConnection = {
  state: ChannelConnectionState;
  reason?: string;
  initiatedBy?: string;
  updatedAt?: string;
};

export const WHATSAPP_BUSINESS_VERTICAL = {
  ALCOHOL: "alcohol",
  APPAREL: "apparel",
  AUTO: "auto",
  BEAUTY: "beauty",
  EDU: "edu",
  ENTERTAIN: "entertain",
  EVENT_PLAN: "event_plan",
  FINANCE: "finance",
  GOVT: "govt",
  GROCERY: "grocery",
  HEALTH: "health",
  HOTEL: "hotel",
  NONPROFIT: "nonprofit",
  ONLINE_GAMBLING: "online_gambling",
  OTC_DRUGS: "otc_drugs",
  OTHER: "other",
  PHYSICAL_GAMBLING: "physical_gambling",
  PROF_SERVICES: "prof_services",
  RESTAURANT: "restaurant",
  RETAIL: "retail",
  TRAVEL: "travel",
} as const;

export type WhatsappBusinessVertical =
  (typeof WHATSAPP_BUSINESS_VERTICAL)[keyof typeof WHATSAPP_BUSINESS_VERTICAL];

export const WHATSAPP_MESSAGING_LIMIT_TIER = {
  TIER_50: "tier_50",
  TIER_250: "tier_250",
  TIER_2K: "tier_2k",
  TIER_10K: "tier_10k",
  TIER_100K: "tier_100k",
  TIER_NOT_SET: "tier_not_set",
  TIER_UNLIMITED: "tier_unlimited",
} as const;

export type WhatsappMessagingLimitTier =
  (typeof WHATSAPP_MESSAGING_LIMIT_TIER)[keyof typeof WHATSAPP_MESSAGING_LIMIT_TIER];

// Wire value from Meta; backend lowercases the Graph response.
export const WHATSAPP_PLATFORM_TYPE = {
  CLOUD_API: "cloud_api",
  SMB_APP: "smb_app",
} as const;

export type WhatsappPlatformType =
  (typeof WHATSAPP_PLATFORM_TYPE)[keyof typeof WHATSAPP_PLATFORM_TYPE];

export const WHATSAPP_SETUP_TYPE = {
  CLOUD_API: "cloud_api",
  COEXISTENCE: "coexistence",
} as const;

export type WhatsappSetupType =
  (typeof WHATSAPP_SETUP_TYPE)[keyof typeof WHATSAPP_SETUP_TYPE];

export const WHATSAPP_COEXISTENCE_SYNC_STATE = {
  PENDING: "pending",
  SYNCING: "syncing",
  COMPLETED: "completed",
} as const;

export type WhatsappCoexistenceSyncState =
  (typeof WHATSAPP_COEXISTENCE_SYNC_STATE)[keyof typeof WHATSAPP_COEXISTENCE_SYNC_STATE];

export type WhatsappCoexistenceSync = {
  state: WhatsappCoexistenceSyncState;
  progress?: number;
  updatedAt?: string;
  contactsRequestId?: string;
  historyRequestId?: string;
  historyDisabled?: boolean;
};

// Interfaces
export type InstagramConfig = {
  username: string;
  userId: string;
  id: string;
  mediaCount: number;
  profilePictureUrl: string;
  biography: string;
  followersCount: number;
  followsCount: number;
  token: string;
  isTokenExpired: boolean;
};

export type WhatsappConfig = {
  token: string;
  isTokenExpired: boolean;
  whatsappProfile?: WhatsappProfile;
  isCoexistence?: boolean;
  coexistenceSync?: WhatsappCoexistenceSync;
  // Provider-agnostic connection lifecycle; absent is treated as connected.
  connection?: ChannelConnection;
  phoneNumber: {
    id: string;
    displayPhoneNumber: string;
    verifiedName: string;
    codeVerificationStatus: string;
    nameStatus: string;
    platformType: WhatsappPlatformType;
    qualityRating: string;
    searchVisibility: string;
    status: string;
    messagingLimitTier: WhatsappMessagingLimitTier;
  };
  businessAccount: {
    id: string;
    name: string;
    ownerBusinessInfo: {
      id: string;
      name: string;
      marketingMessagesOnboardingStatus: {
        status: string;
      };
    };
  };
};

export const PHONE_PROVIDER = {
  DU: "du",
  ETISALAT: "etisalat",
} as const;

export type PhoneProvider =
  (typeof PHONE_PROVIDER)[keyof typeof PHONE_PROVIDER];

export type PhoneConfig = {
  phoneNumber: string;
  provider: PhoneProvider;
  isLandline: boolean;
  displayName: string;
  status: "active" | "inactive" | "pending";
  isTokenExpired: boolean;
};

export type MessengerConfig = {
  pageId: string;
  pageName: string;
  pageAccessToken: string;
  pictureUrl: string;
  isTokenExpired: boolean;
};

export type TikTokConfig = {
  accessToken: string;
  refreshToken: string;
  businessId: string;
  username: string;
  profileImage: string;
  bioDescription: string;
  isBusinessAccount: boolean;
  isVerified: boolean;
  followingCount: number;
  totalLikes: number;
  isTokenExpired: boolean;
};

export type ChannelConfig =
  | InstagramConfig
  | WhatsappConfig
  | PhoneConfig
  | MessengerConfig
  | TikTokConfig;

export type Channel = {
  id: string;
  name: string;
  imageUrl: string;
  type: ChannelType;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
  /** Per-channel override of the workspace AI assignment mode. null inherits. */
  config:
    | InstagramConfig
    | WhatsappConfig
    | PhoneConfig
    | MessengerConfig
    | TikTokConfig;
};
